// Shared building blocks for the phone-first Receiving and Orders screens.
import React from 'react';
import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';

export const BORDER = '#e6e1d8';
export const BORDER_STRONG = '#cfc7b8';
export const PANEL = '#f3f0ea';
export const GOLD = '#9d6f32';
export const GOLD_D = '#7a5525';
export const GOLD_WASH = '#f6ecdb';
export const DARK = '#1a1a1a';
export const MUTED = '#5f5a50';
export const FAINT = '#938b7d';
export const WARN = '#a4620b';
export const WARN_WASH = '#fcf0dc';
export const GREEN = '#2f6b46';
export const GREEN_WASH = '#e5f2ea';
export const RED = '#a83232';
export const BLUE = '#2f5f96';
export const BLUE_WASH = '#e7eff9';
export const FONT_HEADING = "'Georgia', 'Times New Roman', serif";

export const fmt = (value: number) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
export const money = (value: number) => `₹${Math.round(Number(value || 0)).toLocaleString('en-IN')}`;
export const r3 = (value: number) => Math.round(value * 1000) / 1000;
export const toNum = (value: unknown) => { const n = parseFloat(String(value)); return Number.isFinite(n) ? n : 0; };

export function Tile({ onClick, children, dashed, selected, tint, attention }: {
  onClick?: () => void; children: React.ReactNode; dashed?: boolean; selected?: boolean; tint?: boolean; attention?: boolean;
}) {
  return <ButtonBase onClick={onClick} disabled={!onClick} sx={{
    width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left', justifyContent: 'flex-start',
    px: 1.75, py: 1.4, minHeight: 56, borderRadius: 1.5, bgcolor: attention ? WARN_WASH : tint ? PANEL : '#fff',
    border: attention ? '1px solid transparent' : selected ? `2px solid ${GREEN}` : `1px ${dashed ? 'dashed' : 'solid'} ${dashed ? BORDER_STRONG : BORDER}`,
    '&:active': { bgcolor: PANEL }, '&.Mui-disabled': { opacity: 1 },
  }}>{children}</ButtonBase>;
}
export const TileText = ({ title, sub, size = 15 }: { title: React.ReactNode; sub?: React.ReactNode; size?: number }) =>
  <Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontWeight: 500, fontSize: size, color: DARK }}>{title}</Typography>{sub && <Typography component="div" sx={{ fontSize: 12.5, color: MUTED }}>{sub}</Typography>}</Box>;
export const Chevron = () => <Typography sx={{ color: GOLD, fontSize: 22, lineHeight: 1 }}>›</Typography>;

export function Pill({ children, tone = 'gold' }: { children: React.ReactNode; tone?: 'gold' | 'warn' | 'ok' | 'info' }) {
  const tones = { gold: [GOLD_WASH, GOLD_D], warn: [WARN_WASH, WARN], ok: [GREEN_WASH, GREEN], info: [BLUE_WASH, BLUE] } as const;
  return <Box component="span" sx={{ bgcolor: tones[tone][0], color: tones[tone][1], borderRadius: '15px', px: 1.1, py: 0.25, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{children}</Box>;
}

/**
 * A small segmented choice (2-3 options) meant to sit inside a TextField's `endAdornment`, sharing the
 * field's own border instead of taking a separate row — e.g. a quantity field's unit/Trucks choice, or
 * a rate field's Incl./Excl. GST mode. Pass it via `InputProps={{ endAdornment: <SegmentedAdornment ... /> }}`.
 */
export function SegmentedAdornment<T extends string>({ value, onChange, options }: {
  value: T; onChange: (value: T) => void; options: Array<{ value: T; label: string }>;
}) {
  return <Stack direction="row" component="span" sx={{ alignSelf: 'center', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden', bgcolor: PANEL, mr: 1, p: 0.25, gap: 0.25 }}>
    {options.map((option) => {
      const selected = value === option.value;
      return <Box
        component="button" type="button" key={option.value}
        onClick={() => onChange(option.value)}
        sx={{
          border: 0, borderRadius: '6px', whiteSpace: 'nowrap',
          bgcolor: selected ? GOLD : 'transparent', color: selected ? '#fff' : MUTED, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 11, fontWeight: 800, lineHeight: 1.2, minWidth: 34, px: 0.75, py: 0.4,
          '&:hover': { bgcolor: selected ? GOLD_D : BORDER },
        }}
      >{option.label}</Box>;
    })}
  </Stack>;
}
export const CountPill = ({ n }: { n: number }) => <Box sx={{ minWidth: 28, height: 26, px: 1, borderRadius: '15px', bgcolor: WARN_WASH, color: WARN, display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 600 }}>{n}</Box>;
export const SectionLabel = ({ children }: { children: React.ReactNode }) =>
  <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: FAINT, mt: 0.5 }}>{children}</Typography>;

export function ScreenBar({ title, sub, onBack, action }: { title: React.ReactNode; sub?: React.ReactNode; onBack?: () => void; action?: React.ReactNode }) {
  return <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.75, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${BORDER}` }}>
    {onBack && <ButtonBase onClick={onBack} aria-label="Back" sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: PANEL, border: `1px solid ${BORDER}`, fontSize: 20 }}>←</ButtonBase>}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 19, color: DARK }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: 12.5, color: MUTED }}>{sub}</Typography>}
    </Box>
    {action}
  </Stack>;
}
export const ScreenFooter = ({ children }: { children: React.ReactNode }) =>
  <Stack direction="row" alignItems="center" gap={1.25} sx={{ px: 1.75, py: 1.5, borderTop: `1px solid ${BORDER}`, bgcolor: PANEL }}>{children}</Stack>;
export const PrimaryButton = (props: React.ComponentProps<typeof Button>) =>
  <Button variant="contained" {...props} sx={{ minHeight: 48, borderRadius: '15px', px: 2.5, fontWeight: 600, bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D }, ...(props.sx as object) }} />;
export const OutlineButton = (props: React.ComponentProps<typeof Button>) =>
  <Button variant="outlined" {...props} sx={{ minHeight: 48, borderRadius: '15px', color: DARK, borderColor: BORDER_STRONG, fontWeight: 600, ...(props.sx as object) }} />;

/** Number pad for quantities. "C" clears everything typed. */
export function Keypad({ onKey }: { onKey: (key: string) => void }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'del'].map((key) => <ButtonBase key={key} onClick={() => onKey(key)} aria-label={key === 'del' ? 'Delete' : key} sx={{ minHeight: 54, borderRadius: '15px', bgcolor: PANEL, border: `1px solid ${BORDER}`, fontSize: 22, fontWeight: 500 }}>{key === 'del' ? '⌫' : key}</ButtonBase>)}
    <ButtonBase onClick={() => onKey('C')} aria-label="Clear" sx={{ gridColumn: '1 / -1', minHeight: 46, borderRadius: '15px', bgcolor: PANEL, border: `1px solid ${BORDER}`, fontSize: 20, fontWeight: 600, color: RED }}>C</ButtonBase>
  </Box>;
}
/** Keeps only what a quantity can contain: digits and one decimal point. */
export function cleanQuantity(text: string): string {
  const kept = text.replace(/[^0-9.]/g, '');
  const dot = kept.indexOf('.');
  return dot < 0 ? kept : kept.slice(0, dot + 1) + kept.slice(dot + 1).replace(/\./g, '');
}
/** The quantity being entered: shown large, and typeable from the keyboard as well as the pad. */
export function QtyInput({ value, unit, onChange }: { value: string; unit: string; onChange: (next: string) => void }) {
  return <Stack direction="row" alignItems="baseline" justifyContent="center" gap={1}>
    <Box component="input" value={value} placeholder="0" inputMode="decimal" aria-label="Quantity"
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(cleanQuantity(event.target.value))}
      onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.target.select()}
      sx={{ width: `${Math.max(2, value.length + 1)}ch`, minWidth: '2ch', maxWidth: '100%', border: 0, outline: 0, bgcolor: 'transparent', textAlign: 'center', fontFamily: FONT_HEADING, fontSize: 44, lineHeight: 1.1, color: DARK, p: 0, '&::placeholder': { color: FAINT }, '&:focus': { borderBottom: `2px solid ${GOLD}` } }} />
    <Typography component="span" sx={{ fontSize: 16, color: MUTED }}>{unit}</Typography>
  </Stack>;
}
/** Applies a pressed key to the text of a quantity being typed. */
export function applyKey(current: string, key: string): string {
  if (key === 'C') return '';
  if (key === 'del') return current.slice(0, -1);
  if (key === '.' && current.includes('.')) return current;
  return current + key;
}
